import { useState } from "react";

import { Input } from "../../../components/Input/Input";
import { parseReward, rewardToLine, sameReward } from "../../../lib/reward";
import grid from "./HelpTypesEditor.module.css";

interface Props {
  /** The stored reward, the operator's unfinished TEXT, or nothing at all. */
  value: unknown;
  /** `undefined` for a blank box — the caller decides what "none" is. */
  onChange: (next: unknown) => void;
  /** The line under the box. The caller words it: a blank box is fine for the
   *  invitee and a mistake on a ladder step. */
  error?: string;
  modified?: boolean;
  "aria-label": string;
}

/**
 * A reward cell of the Invites section — the invitee's, and each ladder
 * step's: one mono line, spelled as a help type's donor reward is, since the
 * mockup draws the three alike (t-164 config-invites-ladder.html L463, L487).
 *
 * Text that IS a reward is stored parsed; text that is not yet one is stored AS
 * TEXT, and that is the point: the operator keeps what they were typing, the
 * box paints itself red, and the save bar blocks until it becomes a reward or
 * goes away. Nothing else can put a string in a reward key — a string is not a
 * reward — so the state is unambiguous everywhere else that reads the config.
 *
 * Must be rendered inside a `.cell`, which stacks the error line under the box.
 */
export function RewardField({
  value,
  onChange,
  error,
  modified,
  "aria-label": ariaLabel,
}: Props) {
  // The text lives here only while it still means what the config holds: a
  // Discard, a Raw JSON edit or a row removed above replaces the value under
  // the box, and `sameReward` is what notices.
  const [draft, setDraft] = useState<string | null>(null);
  const text =
    draft !== null && sameReward(draft, value) ? draft : rewardToLine(value);

  return (
    <>
      <Input
        className={`${grid.monoInput} ${error ? grid.err : ""}`}
        value={text}
        spellCheck={false}
        onChange={(e) => {
          const raw = e.target.value;
          setDraft(raw);
          const parsed = parseReward(raw);
          onChange(parsed.ok ? parsed.value : raw);
        }}
        modified={!error && modified}
        aria-label={ariaLabel}
        aria-invalid={!!error || undefined}
      />
      {error && <div className={grid.errText}>{error}</div>}
    </>
  );
}
